'use client';

import React, { CSSProperties, ReactNode } from 'react';
import { TOP_NAV_HEIGHT_CSS_VAR } from '@/lib/components/LayoutDimensionsCSSVars';
import { useScrollPosition } from '@/lib/hooks/useScrollPosition';
import { cn } from '@/lib/ui-kit/utils';
import { lerp } from '@/lib/utils/lerp';

export interface StickyHeaderProps {
  /** Lower bound of scroll offset where lerp starts */
  minScroll?: number;
  /** Upper bound of scroll offset where lerp ends */
  maxScroll?: number;
  /** Height at minScroll (px) */
  startHeight?: number;
  /** Height at maxScroll (px) */
  endHeight?: number;
  /** Opacity at minScroll (0–1) */
  startOpacity?: number;
  /** Opacity at maxScroll (0–1) */
  endOpacity?: number;
  /** Additional Tailwind classes for the outer header */
  className?: string;
  /** Classes for inner content wrapper */
  innerClassName?: string;
  /** Custom style resolver for advanced use cases */
  styleResolver?: (t: number) => CSSProperties;
  children: ReactNode;

  offsetTop?: number;
}

export const StickyHeader: React.FC<StickyHeaderProps> = ({
  minScroll = 0,
  maxScroll = 200,
  startHeight = 120,
  endHeight = 60,
  className,
  offsetTop,
  styleResolver,
  children,
}) => {
  const scrollY = useScrollPosition(undefined);

  // Normalize scrollY into [0,1] between minScroll and maxScroll
  const t = Math.min(Math.max((scrollY - minScroll) / (maxScroll - minScroll), 0), 1);

  // Compute dynamic styles
  const dynamicStyles: CSSProperties = styleResolver
    ? styleResolver(t)
    : ({
        ['--scroll-value']: t,
        top: offsetTop !== undefined ? `${offsetTop}px` : `var(${TOP_NAV_HEIGHT_CSS_VAR})`,
        height: lerp(startHeight, endHeight, t),
      } as Record<string, string | number>);

  return (
    <header className={cn('z-25 sticky', className)} style={dynamicStyles}>
      {children}
    </header>
  );
};
